import { FC, ReactElement, useState } from 'react';
import styled from 'styled-components';

import { Caret } from 'shared/icons/Caret';
import { fadeIn, variableFont } from 'styles/theme';

export interface IAccordionItem {
  accordionItemKey: string;
  content: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: (string: string) => void;
  secondaryTitle?: string;
  shouldContentBeVisibleForCrawler?: boolean;
  title: ReactElement | FC | string;
  trackClick?: () => void;
}

interface IAccordionItems {
  accordionKey: string;
  className?: string;
  expandedKey?: string;
  handleToggle?: (accordionKey: string) => void;
  items: IAccordionItem[];
  shouldContentBeVisibleForCrawler?: boolean;
}

const AccordionItem = ({
  accordionItemKey,
  content,
  isExpanded = false,
  onToggle,
  secondaryTitle,
  shouldContentBeVisibleForCrawler = false,
  title,
  trackClick,
}: IAccordionItem) => {
  const handleToggle = () => {
    onToggle(accordionItemKey);
    if (typeof trackClick === 'function') {
      trackClick();
    }
  };

  return (
    <AccordionItem.Container
      key={accordionItemKey}
      $isExpanded={isExpanded}
      $secondaryTitlePresent={!!secondaryTitle}
      data-qa="accordion_item_container"
      className={`accordionItemContainer_${accordionItemKey?.replaceAll('.', '')}`}
    >
      <div
        className={`accordionHeaderButton ${isExpanded ? 'expanded' : ''}`}
        data-qa="accordion_header_button"
        onClick={handleToggle}
        onKeyDown={handleToggle}
        role="button"
        tabIndex={0}
      >
        {isExpanded && !!secondaryTitle && (
          <div
            className="secondaryTitle"
            data-qa="accordion_header_secondary_label"
            role="button"
            tabIndex={0}
          >
            {secondaryTitle}
          </div>
        )}
        <div className={`accordionTitleText ${isExpanded && 'expanded'}`}>
          <div className="primaryTitle" data-qa="accordion_header_label">
            {typeof title === 'function' ? title({}) : title}
          </div>
        </div>
        <Caret
          color="white"
          direction={isExpanded ? 'up' : 'down'}
          data-qa="accordion_caret"
          className="caret"
          size={13}
          strokeWidth={1}
          onClick={handleToggle}
        />
      </div>
      {(isExpanded || shouldContentBeVisibleForCrawler) && (
        <div
          className={`accordionContent ${isExpanded ? 'expanded' : 'hidden'}`}
          data-qa="accordion_content"
        >
          {content}
        </div>
      )}
    </AccordionItem.Container>
  );
};

export const Accordion = ({
  accordionKey,
  items,
  className,
  expandedKey,
  handleToggle,
  shouldContentBeVisibleForCrawler,
}: IAccordionItems) => {
  const [activeKey, setActiveKey] = useState(expandedKey || null);

  const handleAccordionToggle = (accordionItemKey) => {
    setActiveKey(activeKey === accordionItemKey ? null : accordionItemKey);

    if (typeof handleToggle === 'function') {
      handleToggle(accordionItemKey);
    }
  };

  return (
    <Accordion.Container key={accordionKey} data-qa="accordion_container" className={className}>
      {items.map((item) => (
        <AccordionItem
          title={item.title}
          secondaryTitle={item?.secondaryTitle}
          content={item.content}
          accordionItemKey={item.accordionItemKey}
          isExpanded={activeKey === item.accordionItemKey}
          onToggle={() => handleAccordionToggle(item.accordionItemKey)}
          trackClick={item?.trackClick}
          key={item.accordionItemKey}
          shouldContentBeVisibleForCrawler={shouldContentBeVisibleForCrawler}
        />
      ))}
    </Accordion.Container>
  );
};

Accordion.Container = styled.div`
  position: relative;
  background-color: ${({ theme }) => theme.colors.black};
  color: ${({ theme }) => theme.colors.white};
  display: flex;
  flex-direction: column;
  width: 100%;
`;

AccordionItem.Container = styled.div<{ $isExpanded: boolean; $secondaryTitlePresent: boolean }>`
  border-bottom: ${({ theme }) => theme.borders.offBlack2};

  .accordionHeaderButton {
    align-items: center;
    position: relative;
    background-color: ${({ theme }) => theme.colors.black};
    cursor: pointer;
    ${({ $secondaryTitlePresent, $isExpanded }) =>
      $secondaryTitlePresent && $isExpanded ? 'padding: 10px 16px;' : 'padding: 18px 16px;'};
    display: flex;
    justify-content: space-between;
    border-bottom: ${({ theme, $isExpanded }) => ($isExpanded ? 'none' : theme.borders.offBlack)};
    z-index: ${({ theme }) => theme.zIndex.dropdownSelectMenu};
    width: 100%;
  }

  .accordionHeader {
    align-items: center;
    display: flex;
  }

  .accordionTitleText {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .primaryTitle {
    padding: ${({ $secondaryTitlePresent, $isExpanded }) =>
      $secondaryTitlePresent && $isExpanded ? '14px 0 0 0' : '0'};
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    ${variableFont({ size: '12px', height: '12px', fontWeight: '700' })}

    @keyframes slideDown {
      0% {
        transform: translateY(-100%);
      }
      100% {
        transform: translateY(0%);
      }
    }

    animation: ${({ $isExpanded, $secondaryTitlePresent }) =>
      $isExpanded && $secondaryTitlePresent ? `slideDown .25s forwards` : 'none'};
  }

  .secondaryTitle {
    display: flex;
    position: absolute;

    ${variableFont({ size: '10px', height: '11px', fontWeight: '400' })}

    ${fadeIn}
    animation: ${({ $isExpanded }) => ($isExpanded ? `fadeIn .5s` : 'none')};
  }

  .accordionContent {
    z-index: ${({ theme }) => theme.zIndex.accordionDropdownMenuContent};
    overflow-y: scroll;
    ${fadeIn}
    animation: ${({ $isExpanded }) => ($isExpanded ? `fadeIn .25s` : 'none')};

    &.expanded {
      padding: 8px 16px 24px 16px;
      visibility: visible;
      height: 100%;
    }

    &.hidden {
      visibility: hidden;
      height: 0;
    }
  }

  .caret {
    transition: transform 0.3s;
  }
`;
